import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import TheConfigService from '@/services/TheConfigService';
import HostUtils from '@/utils/HostUtils';
import utils from '@/utils/QSUtils';
import AuthTokenHandler from '@/composables/AuthTokenHandler';
import URLUtils from '@/utils/URLUtils';
import { createApp } from 'vue';
import Confirmation from '@/components/modals/Confirmation.vue';

import { miscSettingsStore, userSettingsStore, store } from '@/store';

// Thrown when the launch sequence hits an unrecoverable error - main.ts must not mount the app for this.
export class FatalLaunchError extends Error {}

// 2.6
import OriginalValuesManager from './2.6/OriginalValuesManager';
import MlsFetchManager from './2.6/MlsFetchManager';
import CompsManager from './2.6/CompsManager';
import FormsManager from './2.6/FormsManager';
import SmartExchangeClaimsManager from './2.6/SmartExchangeClaimsManager';
import SalesHistoryManager from './2.6/SalesHistoryManager';
import MlsTokenManager from './2.6/MlsTokenManager';
import NonProdExtrasService from '@/services/2.6/NonProdExtrasService';

// 3.6
import OriginalValuesManagerV36 from './3.6/OriginalValuesManager';
import MlsFetchManagerV36 from './3.6/MlsFetchManager';
import CompsManagerV36 from './3.6/CompsManager';
import FormsManagerV36 from './3.6/FormsManager';
import SmartExchangeClaimsManagerV36 from './3.6/SmartExchangeClaimsManager';
import SalesHistoryManagerV36 from './3.6/SalesHistoryManager';
import MlsTokenManagerV36 from './3.6/MlsTokenManager';
import NonProdExtrasServiceV36 from '@/services/3.6/NonProdExtrasService';

// Generic
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import LogManager from './LogManager';

/**
 * Manages application launch. Handles the incoming local storage values.
 */
export default class LaunchManager {
    private static instance: LaunchManager;
    private configService: TheConfigService;
    private webConfigService: TheWebEnvConfigService;
    private authTokenHandler: AuthTokenHandler | undefined;

    private constructor() {
        this.configService = TheConfigService.getInstance();
        this.webConfigService = TheWebEnvConfigService.getInstance();
    }

    static getInstance(): LaunchManager {
        if (!this.instance) {
            this.instance = new LaunchManager();
        }
        return this.instance;
    }

    async prepare(): Promise<void> {
        await this.setUADVersion();


        if(miscSettingsStore.UADVersion === 3.6) {
            console.log("formCode" , theLocalStorageUtils.get(LocalStorageKeys.formCode));

            const logMsg = LogManager.createLogMsg(14048, 'QS prepare read', 0);
            LogManager.setStrings(logMsg, theLocalStorageUtils.get(LocalStorageKeys.formCode));
            LogManager.sendUsage(logMsg);


            console.log("almCompId" , theLocalStorageUtils.get(LocalStorageKeys.almCompId));
            console.log("defaultComp" , theLocalStorageUtils.get(LocalStorageKeys.defaultComp));
            console.log("callingProductId" , theLocalStorageUtils.get(LocalStorageKeys.callingProductId));
            console.log("p2id" , theLocalStorageUtils.get(LocalStorageKeys.p2id));
            console.log("uuid" , theLocalStorageUtils.get(LocalStorageKeys.uuid));
        }


        await this.handleLocalStorage();
        await this.handleConfigs();
        await this.handleAuthentications();
        await userSettingsStore.FetchSettings();


        //**********************************************************
        // Handle any specific logic for UADVersion 2.6 before saving the form code
        //**********************************************************
        const config = await this.configService.getConfig();
        const hardcodedto36FormCode = "1004UAD36";

        if (miscSettingsStore.UADVersion === 2.6) {
            await OriginalValuesManager.fetchAndStore();
            await MlsFetchManager.fetchAndStore();
            await SalesHistoryManager.fetchSelectionsAndStore();

            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '');
            await FormsManager.fetchAndStoreAdditionalFields();
            await SmartExchangeClaimsManager.getClaims();

            await CompsManager.fetchAndStore();
            await MlsFetchManager.cleanupMlsStore();
        } else if (miscSettingsStore.UADVersion === 3.6) {

            await this.handleAnnouncementsModal();
            await this.handleAnnouncementsBanner();

            await OriginalValuesManagerV36.fetchAndStore();
            await MlsFetchManagerV36.fetchAndStore();
            await SalesHistoryManagerV36.fetchSelectionsAndStore();



            //**********************************************************
            // Handle any specific logic for UADVersion 2.6 before saving the form code
            //**********************************************************
            //await FormsManagerV36.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '');
            //await FormsManagerV36.fetchAndStoreFormFieldsAdditionalDetails(theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '');
            
            const logMsg = LogManager.createLogMsg(14048, 'QS prepare read hardcodedto36FormCode', 0);
            LogManager.setStrings(logMsg, hardcodedto36FormCode);
            LogManager.sendUsage(logMsg);
            
            await FormsManagerV36.fetchAndStoreFormFields(hardcodedto36FormCode);
            await FormsManagerV36.fetchAndStoreFormFieldsAdditionalDetails(hardcodedto36FormCode);


            await FormsManagerV36.fetchAndStoreSubjectCompRelationship(false);
            await SmartExchangeClaimsManagerV36.getClaims();

            await CompsManagerV36.fetchAndStore();
            await MlsFetchManagerV36.cleanupMlsStore();
        }

    }

    async cleanup(): Promise<void> {
        if (this.authTokenHandler) {
            this.authTokenHandler.stop();
        }
    }

    private async setUADVersion(): Promise<void> {
        // .path includes the querystring (e.g. "/3.6?cb=<guid>"); .pathname does not - use
        // .pathname so a cache-busting query param doesn't break the version parse below.
        let uadVersion = URLUtils.parseURL(URLUtils.getURL())?.pathname || '/';
        if (uadVersion === '/') {
            uadVersion = 2.6;
        } else {
            uadVersion = Number(uadVersion?.replace('/', '')) || 2.6;
        }
        await miscSettingsStore.UADVersionUpdate(uadVersion);
    }

    private async waitForAlmCompId(): Promise<void> {
        const maxAttempts = 10;
        const delayMs = 250;
        for (let attempt = 0; attempt < maxAttempts && !theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.almCompId); attempt++) {
            await utils.sleep(delayMs);
        }
    }

    private showFatalError(message: string): void {
        console.log("Fatal error: " + message);
    }

    private async handleAnnouncementsModal(): Promise<void> {
        const dontShowAgain = String(theLocalStorageUtils.get(LocalStorageKeys.showModalAnnouncements)) === 'false';
        if (!dontShowAgain) {
            await miscSettingsStore.ShowAnnouncementsModalUpdate(true);
        }
    }

    private async handleAnnouncementsBanner(): Promise<void> {
        const dontShowAgain = String(theLocalStorageUtils.get(LocalStorageKeys.showBannerAnnouncements)) === 'false';
        if (!dontShowAgain) {
            await miscSettingsStore.ShowAnnouncementsBannerUpdate(true);
        }
    }

    private async handleLocalStorage(): Promise<void> {

        const config = await this.configService.getConfig();

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.callingProductId)) {
            theLocalStorageUtils.save(LocalStorageKeys.callingProductId, config.callingProductId ?? '0');
        }

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.almCompId)) {
            // 3.6 only - the host can write alm-comp-id to local storage slightly after our own
            // load starts, so poll briefly for it before falling back to the config default.
            if (miscSettingsStore.UADVersion === 3.6) {
                await this.waitForAlmCompId();

                if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.almCompId)) {
                    this.showFatalError('Error, No Alm Comp Id Found.');
                    throw new FatalLaunchError('LaunchManager.handleLocalStorage(), No alm-comp-id found in local storage after retries.');
                }
            } else {
                theLocalStorageUtils.save(LocalStorageKeys.almCompId, config.almCompId);
            }
        }

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.defaultComp)) {
            theLocalStorageUtils.save(LocalStorageKeys.defaultComp, config.defaultComp);
        }



        

        // let expectedFormCode = miscSettingsStore.UADVersion === 3.6 ? "1004UAD36" : config.formCode;
        // const currentFormCodeRaw = theLocalStorageUtils.get(LocalStorageKeys.formCode);
        // const currentFormCode = typeof currentFormCodeRaw === 'string' ? currentFormCodeRaw.trim() : '';
        // let shouldUpdateFormCode = false;

        // if (miscSettingsStore.UADVersion === 2.6) {
        //     if (!currentFormCode) {
        //         shouldUpdateFormCode = true;
        //     } else {
        //         shouldUpdateFormCode = currentFormCode.includes('36');
        //     }
        // } else if (miscSettingsStore.UADVersion === 3.6) {
        //     if (!currentFormCode || currentFormCodeRaw === "UAD-3.6-AR-1.000") {
        //         shouldUpdateFormCode = true;
        //     } else {
        //         shouldUpdateFormCode = !currentFormCode.includes('36');
        //     }
        // }

        //**********************************************************
        // Handle any specific logic for UADVersion 2.6 before saving the form code
        //**********************************************************
        if (miscSettingsStore.UADVersion === 3.6) {
            //expectedFormCode = config.formCode36;
            let expectedFormCode = "1004UAD36";

            let logMsg = LogManager.createLogMsg(14048, '3.6 expectedFormCode form code from config', 0);
            LogManager.setStrings(logMsg, config.formCode36);
            LogManager.sendUsage(logMsg);

            
            logMsg = LogManager.createLogMsg(14048, '3.6 expectedFormCode form code', 0);
            LogManager.setStrings(logMsg, expectedFormCode);
            LogManager.sendUsage(logMsg);

            theLocalStorageUtils.save(LocalStorageKeys.formCode, expectedFormCode);
        } else {

            //if (expectedFormCode && shouldUpdateFormCode) {

                if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.formCode)) {

                    const logMsg = LogManager.createLogMsg(14048, '2.6 expectedFormCode form code', 0);
                    LogManager.setStrings(logMsg, config.formCode);
                    LogManager.sendUsage(logMsg);

                    theLocalStorageUtils.save(LocalStorageKeys.formCode, config.formCode);
                }
            //}

        }



        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.p2id) && config.p2id) {
            theLocalStorageUtils.save(LocalStorageKeys.p2id, config.p2id);
        }

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.uuid)) {
            const uuid = utils.uuid();
            theLocalStorageUtils.save(LocalStorageKeys.uuid, uuid);
        }

        this.checkExpected();
    }

    private async handleConfigs(): Promise<void> {
        const webConfig = await this.webConfigService.getConfigByHost();

        console.log(`LaunchManager.handleConfigs(), Host URL[${URLUtils.getURL()}].`);

        if (webConfig.hostname) {
            console.log(`LaunchManager.handleConfigs(), Identified hostname[${webConfig.hostname}].`);

            if (webConfig.urls) {
                if (webConfig.urls.quickSourceApi) {
                    console.log(`LaunchManager.handleConfigs(), QS API URL[${webConfig.urls.quickSourceApi}].`);
                } else {
                    console.log('LaunchManager.handleConfigs(), Error, Did not get QS API URL.');
                }
            } else {
                console.log('LaunchManager.handleConfigs(), Error, Did not get service URLs.');
            }
        } else {
            console.log('LaunchManager.handleConfigs(), Error, Did not identify hostname.');
        }
    }

    private async handleAuthentications(): Promise<void> {
        this.authTokenHandler = new AuthTokenHandler();
        await this.authTokenHandler.start().then(async () => {
            if (HostUtils.isNone()) {
                await this.getAndSaveSmartExchangeToken();
            }
        });

        if (miscSettingsStore.UADVersion === 2.6) {
            await MlsTokenManager.getToken(true);
        } else {
            await MlsTokenManagerV36.getToken(true);
        }
    }

    private async getAndSaveSmartExchangeToken(): Promise<boolean> {
        const webConfig = await this.webConfigService.getConfigByHost();
        const config = await this.configService.getConfig();

        let userToken = '';
        if (miscSettingsStore.UADVersion === 2.6) {
            const service = new NonProdExtrasService(webConfig);
            userToken = await service.getSmartExchangeUserToken(config.username, config.password);
        } else {
            const service = new NonProdExtrasServiceV36(webConfig);
            userToken = await service.getSmartExchangeUserToken(config.username, config.password);
        }

        if (userToken) {
            theLocalStorageUtils.save(LocalStorageKeys.seAccessToken, userToken);
            return true;
        }

        console.log('LaunchManager.getAndSaveSmartExchangeToken(), Failed to get a token.');
        return false;
    }

    private checkExpected(): void {
        if (HostUtils.isTotal()) {
            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.totalUpdateNumber)) {
                console.log('LaunchManager.checkExpected(), No Total update number in local storage.');
            }

            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.almComps)) {
                console.log('LaunchManager.checkExpected(), No comps local storage.');
            }

            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.qsAccessToken)) {
                console.log('LaunchManager.checkExpected(), No QuickSource access token in local storage.');
            }

            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.qsRefreshToken)) {
                console.log('LaunchManager.checkExpected(), No QuickSource refresh token in local storage.');
            }

            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.qsAccessTokenExpiration)) {
                console.log('LaunchManager.checkExpected(), No QuickSource access token expiration in local storage.');
            }

            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.seAccessToken)) {
                console.log('LaunchManager.checkExpected(), No Smart Exchange access token in local storage.');
            }

            if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.p2id)) {
                console.log('LaunchManager.checkExpected(), No p2id in local storage.');
            }
        }

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.almCompId)) {
            console.log('LaunchManager.checkExpected(), No Comp Id in local storage.');
        }

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.defaultComp)) {
            console.log('LaunchManager.checkExpected(), No current comp number in local storage.');
        }

        if (!theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.formCode)) {
            console.log('LaunchManager.checkExpected(), No form code in local storage.');
        }
    }
}
